import { useId } from "react";
import { AlertCircle, Check, CircleHelp, Loader2, PencilLine, Sparkles } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { SearchableSelect } from "@/lib/picker-search";
import type { DetectedField, DetectionResponse } from "@/components/use-launch-account-detection";
import { LaunchPageDiscovery, type LaunchPageDiscoveryProps } from "@/components/launch-page-discovery";

type CatalogAccount = { value: string; label: string; accountName: string };
type FieldKey = "vertical" | "page" | "pixel" | "budget" | "namingBrand" | "namingProductLine" | "leadSource" | "pageName";
type DetectionState = "idle" | "loading" | "success" | "error";
type FieldStatusProps = {
  state: "checking" | "detected" | "selection" | "missing" | "manual" | "catalog" | "error" | "idle";
  text: string;
  note?: string;
};

export type AddLaunchAccountFormProps = {
  pageDiscovery?: LaunchPageDiscoveryProps;
  accountId: string;
  accountName: string;
  onAccountIdChange: (value: string) => void;
  onAccountNameChange: (value: string) => void;
  metaPages?: Array<{ id?: string; name?: string }>;
  availableAccounts: CatalogAccount[];
  catalogLoading: boolean;
  catalogError: boolean;
  catalogFetching: boolean;
  onRetryCatalog: () => void;
  detectionStatus: DetectionState;
  detectionResult: DetectionResponse | null;
  onRetryDetection: () => void;
  manualFields: ReadonlySet<string>;
  vertical: string;
  onVerticalChange: (value: string) => void;
  pageId: string;
  onPageIdChange: (value: string) => void;
  pageName: string;
  onPageNameChange: (value: string) => void;
  pixelId: string;
  onPixelIdChange: (value: string) => void;
  budgetCap: string;
  onBudgetCapChange: (value: string) => void;
  namingBrand: string;
  onNamingBrandChange: (value: string) => void;
  namingProductLine: string;
  onNamingProductLineChange: (value: string) => void;
  namingPrefix: string;
  onNamingPrefixChange: (value: string) => void;
  leadSource: string;
  onLeadSourceChange: (value: string) => void;
  defaultTemplate: string;
  onDefaultTemplateChange: (value: string) => void;
  templates: Array<{ id: string }>;
  saving: boolean;
  onSave: () => void;
  onCancel: () => void;
};

function FieldStatus({ state, text, note, testId }: FieldStatusProps & { testId?: string }) {
  const icon = state === "checking"
    ? <Loader2 className="h-3.5 w-3.5 shrink-0 animate-spin" aria-hidden="true" />
    : state === "detected" || state === "catalog"
      ? <Check className="h-3.5 w-3.5 shrink-0" aria-hidden="true" />
      : state === "selection" || state === "error"
        ? <AlertCircle className="h-3.5 w-3.5 shrink-0" aria-hidden="true" />
        : state === "manual"
          ? <PencilLine className="h-3.5 w-3.5 shrink-0" aria-hidden="true" />
          : <CircleHelp className="h-3.5 w-3.5 shrink-0" aria-hidden="true" />;
  const color = state === "detected" || state === "catalog"
    ? "text-emerald-800"
    : state === "selection" || state === "error"
      ? "text-amber-800"
      : state === "checking"
        ? "text-sky-800"
        : "text-muted-foreground";
  return (
    <div className={`flex min-w-0 items-start gap-1.5 text-[11px] leading-4 ${color}`} data-testid={testId}>
      {icon}
      <span className="min-w-0 break-words [overflow-wrap:anywhere]">
        <span className="font-medium">{text}</span>
        {note ? <span className="text-muted-foreground"> · {note}</span> : null}
      </span>
    </div>
  );
}

export function AddLaunchAccountForm(props: AddLaunchAccountFormProps) {
  const idPrefix = useId();
  const {
    accountId, accountName, onAccountIdChange, availableAccounts, catalogLoading, catalogError, catalogFetching,
    onRetryCatalog, detectionStatus, detectionResult, onRetryDetection, manualFields, vertical, onVerticalChange,
    pageId, onPageIdChange, pageName, onPageNameChange, pixelId, onPixelIdChange, budgetCap, onBudgetCapChange,
    namingBrand, onNamingBrandChange, namingProductLine, onNamingProductLineChange, namingPrefix, onNamingPrefixChange,
    leadSource, onLeadSourceChange, defaultTemplate, onDefaultTemplateChange, templates, saving, onSave, onCancel,
  } = props;
  const responseMatchesAccount = !!detectionResult && String(detectionResult.connectedAccountId) === accountId;
  const currentDetectionStatus: DetectionState = accountId && (
    detectionStatus === "idle" || (detectionStatus === "success" && !responseMatchesAccount)
  ) ? "loading" : detectionStatus;
  const detection = currentDetectionStatus === "success" && responseMatchesAccount ? detectionResult?.detection : null;
  const checking = currentDetectionStatus === "loading";
  const exactPageName = pageId.trim()
    ? props.metaPages?.find((page) => String(page.id) === pageId.trim())?.name
    : undefined;

  const fieldStatus = (
    key: FieldKey,
    value: string,
    source: { value: string | null; status: string; note?: string } | undefined,
    options?: { detectable?: boolean; unambiguous?: boolean; ambiguousEvidence?: boolean; emptyText?: string },
  ): FieldStatusProps => {
    if (checking && options?.detectable !== false) return { state: "checking" as const, text: "Checking" };
    if (currentDetectionStatus === "error" && options?.detectable !== false) {
      return { state: "error" as const, text: "Could not check", note: "Enter manually or retry" };
    }
    if (options?.detectable === false || currentDetectionStatus === "idle") {
      return { state: "idle" as const, text: options?.emptyText ?? "Manual" };
    }
    const isManual = manualFields.has(key) || (!!value.trim() && !!source?.value && value.trim() !== source.value);
    if (isManual) return { state: "manual" as const, text: "Manual override" };
    if (source?.status === "detected" && source.value && options?.unambiguous !== false && value.trim() === source.value) {
      return { state: "detected" as const, text: "Detected", note: source.note };
    }
    if (source?.status === "ambiguous" || source?.status === "majority" || options?.ambiguousEvidence) {
      return value.trim()
        ? { state: "manual" as const, text: "Manual override", note: source?.note }
        : { state: "selection" as const, text: "Needs selection", note: source?.note };
    }
    if (source?.status === "none" || !source?.value) {
      return { state: "missing" as const, text: options?.emptyText ?? "Not found", note: source?.note };
    }
    return { state: "missing" as const, text: "Enter manually", note: source?.note };
  };

  const pageStatus = fieldStatus("page", pageId, detection?.pageId, {
    unambiguous: !detection?.pageId.candidates || detection.pageId.candidates.length <= 1,
    ambiguousEvidence: !!detection?.pageId.candidates && detection.pageId.candidates.length > 1,
  });
  const selectedOrganicPage = props.pageDiscovery?.organicPages?.find(page => page.pageId === pageId.trim());
  const pixelStatus = fieldStatus("pixel", pixelId, detection?.pixelId, {
    unambiguous: !detection?.pixelId.candidates || detection.pixelId.candidates.length <= 1,
    ambiguousEvidence: !!detection?.pixelId.candidates && detection.pixelId.candidates.length > 1,
  });
  const namingSource = detection?.naming;
  const brandStatus = fieldStatus("namingBrand", namingBrand, namingSource
    ? { value: namingSource.value?.brand ?? null, status: namingSource.status, note: namingSource.note }
    : undefined);
  const productStatus = fieldStatus("namingProductLine", namingProductLine, namingSource
    ? { value: namingSource.value?.product_line ?? null, status: namingSource.status, note: namingSource.note }
    : undefined);
  const verticalStatus = fieldStatus("vertical", vertical, detection?.vertical);
  const budgetSource = detection?.dailyBudgetCapMinor
    ? {
        value: detection.dailyBudgetCapMinor.value == null ? null : (detection.dailyBudgetCapMinor.value / 100).toFixed(2),
        status: detection.dailyBudgetCapMinor.status,
        note: detection.dailyBudgetCapMinor.note,
      }
    : undefined;
  const budgetStatus = fieldStatus("budget", budgetCap, budgetSource);
  const leadStatus = fieldStatus("leadSource", leadSource, detection?.leadSource);
  const pageNameStatus = exactPageName && pageName.trim() === exactPageName
    ? { state: "catalog" as const, text: "Exact Meta ID match" }
    : manualFields.has("pageName")
      ? { state: "manual" as const, text: "Manual override" }
      : { state: "idle" as const, text: "Manual or optional" };
  const candidates = (field: DetectedField | undefined) =>
    field?.candidates?.filter((candidate) => /^\d+$/.test(candidate.value)) ?? [];
  const pageCandidates = candidates(detection?.pageId);
  const pixelCandidates = candidates(detection?.pixelId);
  const formBusy = checking;
  const noSuggestedValues = !!detection && [
    detection.pageId, detection.pixelId, detection.vertical, detection.dailyBudgetCapMinor, detection.naming,
    ...(vertical === "leadgen" && detection.leadSource ? [detection.leadSource] : []),
  ].every((field) => field.status === "none");

  const fieldClass = "min-w-0 space-y-1.5 rounded-lg border border-border/70 bg-background/70 p-3";
  const renderStatus = (status: ReturnType<typeof fieldStatus>, id: string, busy = formBusy) => (
    <div aria-busy={busy} className="min-h-4" data-checking-area={busy ? "true" : undefined}>
      <FieldStatus {...status} testId={id} />
    </div>
  );

  return (
    <section className="overflow-hidden rounded-xl border border-border bg-card shadow-sm" data-testid="form-add-launch-account">
      <header className="border-b bg-muted/30 px-4 py-4 sm:px-5">
        <div className="flex items-start gap-3">
          <span className="mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-primary/10 text-primary">
            <Sparkles className="h-4 w-4" aria-hidden="true" />
          </span>
          <div className="min-w-0">
            <h3 className="text-sm font-semibold tracking-tight">Set up a paid ad identity</h3>
            <p className="mt-1 max-w-3xl text-xs leading-5 text-muted-foreground">
              Choose where ads are paid for, which Facebook Page appears on them, and the campaign type. This does not change the brand’s Analytics connection.
            </p>
          </div>
        </div>
      </header>

      <div className="space-y-4 p-3 sm:space-y-5 sm:p-5">
        <div className="space-y-4">
          <div className={fieldClass + " sm:col-span-2"}>
            <Label htmlFor={`${idPrefix}-account`}>Paid ad account <span className="font-normal text-muted-foreground">· pays for ads</span></Label>
            {catalogLoading ? (
              <div className="flex min-h-10 items-center gap-2 rounded-md border bg-muted/30 px-3 text-xs text-muted-foreground" role="status" data-testid="loading-available-launch-accounts" aria-busy="true">
                <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" /> Loading available Windsor accounts
              </div>
            ) : catalogError ? (
              <div className="flex min-w-0 flex-wrap items-center justify-between gap-2 rounded-md border border-destructive/30 bg-destructive/5 px-3 py-2" role="alert" data-testid="error-available-launch-accounts">
                <p className="min-w-0 text-xs text-muted-foreground">Available Windsor accounts could not be loaded.</p>
                <Button type="button" size="sm" variant="outline" onClick={onRetryCatalog} disabled={catalogFetching} data-testid="button-retry-available-launch-accounts">
                  {catalogFetching && <Loader2 className="mr-2 h-3.5 w-3.5 animate-spin" aria-hidden="true" />}
                  Retry
                </Button>
              </div>
            ) : (
              <>
                <SearchableSelect
                  value={accountId || "__none__"}
                  onValueChange={onAccountIdChange}
                  triggerId={`${idPrefix}-account`}
                  ariaLabel="Paid ad account"
                  testId="select-new-launch-account-id"
                  options={[
                    { value: "__none__", label: availableAccounts.length ? "Choose the account that pays for ads" : "No available ad accounts" },
                    ...availableAccounts,
                  ]}
                />
                {availableAccounts.length === 0 && (
                  <p className="text-xs leading-4 text-muted-foreground" data-testid="empty-available-launch-accounts">
                    No unconfigured paid ad accounts are available. To add a Page to an existing account, cancel and use “Add a Facebook Page” in that account’s setup.
                  </p>
                )}
              </>
            )}
            <p className="text-[11px] leading-4 text-muted-foreground">The account name is supplied automatically. Analytics remains unchanged.</p>
          </div>

          <div className={`${fieldClass} sm:col-span-2`} aria-busy={formBusy}>
            <Label htmlFor={`${idPrefix}-vertical`}>Campaign type</Label>
            <SearchableSelect
              value={vertical || "__none__"}
              onValueChange={onVerticalChange}
              triggerId={`${idPrefix}-vertical`}
              ariaLabel="Campaign type"
              testId="select-new-launch-account-vertical"
              options={[{ value: "__none__", label: "Choose a campaign type" }, { value: "ecom", label: "Sales / ecommerce" }, { value: "leadgen", label: "Lead generation" }]}
            />
            {renderStatus(verticalStatus, "status-new-account-vertical")}
          </div>

          <div className={`${fieldClass} sm:col-span-2`} aria-busy={formBusy}>
            {props.pageDiscovery ? <LaunchPageDiscovery key={accountId} {...props.pageDiscovery} immediate value={pageId} label="Facebook Page — appears on the ads" testId="new-account-page-discovery" onSelect={(page) => {
              onPageIdChange(page.pageId);
              onPageNameChange(page.name);
            }} /> : <div>
              <Label>Facebook Page <span className="font-normal text-muted-foreground">· appears on the ads</span></Label>
              <p className="text-xs text-muted-foreground">Connect a Page or enter its ID in Advanced settings.</p>
            </div>}
            {pageName.trim() && <p className="text-sm font-medium text-foreground" data-testid="text-new-account-selected-page">{pageName}</p>}
            {renderStatus(selectedOrganicPage
              ? { state: "catalog", text: "Selected from Windsor Organic" }
              : pageStatus, "status-new-account-page")}
            {pageCandidates.length > 1 && !pageId.trim() && (
              <div className="space-y-2 rounded-md bg-amber-50/70 p-2.5" data-testid="candidates-new-account-page">
                <p className="text-xs font-medium text-amber-950">Choose the Page observed in recent campaigns</p>
                <div className="flex flex-wrap gap-1.5">
                  {pageCandidates.map((candidate) => {
                    const name = props.metaPages?.find((page) => String(page.id) === candidate.value)?.name;
                    return <Button key={candidate.value} type="button" size="sm" variant="outline" onClick={() => {
                      onPageIdChange(candidate.value);
                      if (name && !pageName.trim()) onPageNameChange(name);
                    }} data-testid={`button-new-account-page-candidate-${candidate.value}`} className="max-w-full">
                      <span className="max-w-full break-all">{name ? `${name} · ` : ""}{candidate.value} · {Math.round(candidate.share * 100)}%</span>
                    </Button>;
                  })}
                </div>
                {detection?.pageId.note && <p className="break-words text-[11px] leading-4 text-muted-foreground [overflow-wrap:anywhere]">{detection.pageId.note}</p>}
              </div>
            )}
          </div>

          <div className={`${fieldClass} sm:col-span-2`} aria-busy={formBusy}>
            <Label htmlFor={`${idPrefix}-pixel`}>Meta Pixel ID <span className="font-normal text-muted-foreground">· for conversion tracking, if used</span></Label>
            <Input id={`${idPrefix}-pixel`} value={pixelId} onChange={(event) => onPixelIdChange(event.target.value)} inputMode="numeric" placeholder="Enter Pixel ID" data-testid="input-new-launch-account-pixel" />
            {renderStatus(pixelStatus, "status-new-account-pixel")}
            {pixelCandidates.length > 1 && !pixelId.trim() && (
              <div className="space-y-2 rounded-md bg-amber-50/70 p-2.5" data-testid="candidates-new-account-pixel">
                <p className="text-xs font-medium text-amber-950">Choose the pixel observed in recent campaigns</p>
                <div className="flex flex-wrap gap-1.5">
                  {pixelCandidates.map((candidate) => <Button key={candidate.value} type="button" size="sm" variant="outline" onClick={() => onPixelIdChange(candidate.value)} data-testid={`button-new-account-pixel-candidate-${candidate.value}`} className="max-w-full">
                    <span className="break-all">{candidate.value} · {Math.round(candidate.share * 100)}%</span>
                  </Button>)}
                </div>
                {detection?.pixelId.note && <p className="break-words text-[11px] leading-4 text-muted-foreground [overflow-wrap:anywhere]">{detection.pixelId.note}</p>}
              </div>
            )}
          </div>

          <details className="group rounded-lg border border-border/70 bg-muted/20">
            <summary className="flex cursor-pointer list-none items-center justify-between gap-3 px-4 py-3 text-sm font-medium [&::-webkit-details-marker]:hidden">
              <span>Advanced settings</span>
              <span className="text-xs font-normal text-muted-foreground">Manual IDs, budget and naming options</span>
            </summary>
            <div className="grid min-w-0 grid-cols-1 gap-3 border-t p-3 sm:grid-cols-2">
              <div className={fieldClass}>
                <Label htmlFor={`${idPrefix}-page`}>Manual Facebook Page ID</Label>
                <Input id={`${idPrefix}-page`} value={pageId} onChange={(event) => onPageIdChange(event.target.value)} inputMode="numeric" placeholder="Enter Page ID" data-testid="input-new-launch-account-page-id" />
              </div>
              <div className={fieldClass}>
                <Label htmlFor={`${idPrefix}-page-name`}>Manual Page name</Label>
                <Input id={`${idPrefix}-page-name`} value={pageName} onChange={(event) => onPageNameChange(event.target.value)} placeholder="Optional Page name" data-testid="input-new-launch-account-page-name" />
                {renderStatus(pageNameStatus, "status-new-account-page-name", false)}
                <p className="text-[11px] leading-4 text-muted-foreground">Optional manual name or exact match from connected Meta Pages.</p>
              </div>
              <div className={fieldClass}>
                <Label htmlFor={`${idPrefix}-account-name`}>Account name override</Label>
                <Input id={`${idPrefix}-account-name`} value={accountName} onChange={(event) => props.onAccountNameChange(event.target.value)} placeholder="Use catalog account name" data-testid="input-new-launch-account-name" />
                <FieldStatus state="manual" text="Optional override" />
              </div>
              <div className={fieldClass} aria-busy={formBusy}>
                <Label htmlFor={`${idPrefix}-budget`}>Daily budget cap <span className="font-normal text-muted-foreground">(USD)</span></Label>
                <Input id={`${idPrefix}-budget`} value={budgetCap} onChange={(event) => onBudgetCapChange(event.target.value)} inputMode="decimal" placeholder="Optional cap" data-testid="input-new-launch-account-budget-cap" />
                {renderStatus(budgetStatus, "status-new-account-budget")}
              </div>
              <div className={fieldClass} aria-busy={formBusy}>
                <Label htmlFor={`${idPrefix}-naming-brand`}>Naming brand code</Label>
                <Input id={`${idPrefix}-naming-brand`} value={namingBrand} onChange={(event) => onNamingBrandChange(event.target.value)} placeholder="Optional brand code" data-testid="input-new-launch-account-naming-brand" />
                {renderStatus(brandStatus, "status-new-account-naming-brand")}
              </div>
              <div className={fieldClass} aria-busy={formBusy}>
                <Label htmlFor={`${idPrefix}-naming-product`}>Naming product line</Label>
                <Input id={`${idPrefix}-naming-product`} value={namingProductLine} onChange={(event) => onNamingProductLineChange(event.target.value)} placeholder="Optional product line" data-testid="input-new-launch-account-naming-product-line" />
                {renderStatus(productStatus, "status-new-account-naming-product-line")}
              </div>
              <div className={fieldClass}>
                <Label htmlFor={`${idPrefix}-prefix`}>Campaign naming prefix</Label>
                <Input id={`${idPrefix}-prefix`} value={namingPrefix} onChange={(event) => onNamingPrefixChange(event.target.value)} placeholder="Optional prefix" data-testid="input-new-launch-account-prefix" />
                <FieldStatus state="idle" text="Manual" />
              </div>
              <div className={fieldClass}>
                <Label htmlFor={`${idPrefix}-template`}>Default template</Label>
                <SearchableSelect
                  value={defaultTemplate || "__none__"}
                  onValueChange={onDefaultTemplateChange}
                  triggerId={`${idPrefix}-template`}
                  ariaLabel="Default template"
                  testId="select-new-launch-account-template"
                  options={[{ value: "__none__", label: "Not set" }, ...templates.map((template) => ({ value: template.id, label: template.id }))]}
                />
                <FieldStatus state="idle" text="Manual selection only" note="Windsor does not suggest templates" />
              </div>
              {vertical === "leadgen" && (
                <div className={fieldClass + " sm:col-span-2"} aria-busy={formBusy}>
                  <Label htmlFor={`${idPrefix}-lead-source`}>Lead source</Label>
                  <Input id={`${idPrefix}-lead-source`} value={leadSource} onChange={(event) => onLeadSourceChange(event.target.value)} placeholder="Lead counter, for example CRM MQLs" data-testid="input-new-launch-account-lead-source" />
                  {renderStatus(leadStatus, "status-new-account-lead-source")}
                </div>
              )}
            </div>
          </details>
        </div>

        <div className={`flex min-w-0 flex-col gap-3 rounded-lg border px-3 py-3 sm:flex-row sm:items-center sm:justify-between sm:px-4 ${currentDetectionStatus === "error" ? "border-destructive/30 bg-destructive/5" : "border-primary/15 bg-primary/[0.035]"}`} data-testid="new-account-detection-status" aria-live="polite">
          <div className="flex min-w-0 items-start gap-2.5">
            {checking ? <Loader2 className="mt-0.5 h-4 w-4 shrink-0 animate-spin text-primary" aria-hidden="true" /> : currentDetectionStatus === "error" ? <AlertCircle className="mt-0.5 h-4 w-4 shrink-0 text-amber-800" aria-hidden="true" /> : <Sparkles className="mt-0.5 h-4 w-4 shrink-0 text-primary" aria-hidden="true" />}
            <p className="min-w-0 break-words text-xs leading-5 text-foreground [overflow-wrap:anywhere]">
              {checking ? "Checking available launch fields with Windsor…" :
                currentDetectionStatus === "error" ? "Windsor could not check these fields. Nothing was saved; enter values manually or retry." :
                  currentDetectionStatus === "success" ? `Read-only detection across ${detectionResult?.windowDays ?? "recent"} days. Each field shows its own result.` :
                    accountId ? "Waiting for Windsor field check…" : "Choose an account to check recent campaign settings."}
              {noSuggestedValues ? <span className="block text-muted-foreground" data-testid="text-new-account-no-detection">No values were available to suggest. Enter them manually.</span> : null}
            </p>
          </div>
          {accountId && <Button type="button" size="sm" variant="outline" className="shrink-0 self-start sm:self-auto" onClick={onRetryDetection} disabled={checking} data-testid="button-retry-new-account-detection">
            {checking ? "Checking…" : currentDetectionStatus === "success" ? "Detect again" : "Retry detection"}
          </Button>}
        </div>

        <div className="flex flex-col-reverse gap-2 border-t pt-4 sm:flex-row sm:items-center">
          <Button type="button" disabled={saving || checking || catalogError || catalogFetching || !availableAccounts.some((account) => account.value === accountId) || !accountName.trim() || !/^\d+$/.test(pageId.trim())} onClick={onSave} data-testid="button-save-new-launch-account" className="sm:min-w-44">
            {saving && <Loader2 className="mr-2 h-4 w-4 animate-spin" aria-hidden="true" />}Save launch account
          </Button>
          <Button type="button" size="sm" variant="ghost" onClick={onCancel} className="sm:order-first">Cancel</Button>
          <p className="text-[11px] leading-4 text-muted-foreground sm:ml-auto">No changes are saved until you choose Save.</p>
        </div>
      </div>
    </section>
  );
}
